import styled, { css, keyframes } from "styled-components";
import { Check } from "@styled-icons/feather/Check";
import { ChevronDown } from "@styled-icons/feather/ChevronDown";

const rise = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

const shimmer = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const WILLOW_MARK_NAVY = "#0b1138";
const WILLOW_MARK_BLUE = "#0039d0";

const hop = keyframes`
  0%, 100% { transform: translateY(0) scale(1, 1); }
  12% { transform: translateY(0) scale(1.22, 0.78); }
  42% { transform: translateY(-120%) scale(0.92, 1.08); }
  70% { transform: translateY(0) scale(1.18, 0.82); }
  82% { transform: translateY(0) scale(1, 1); }
`;

const enter = css<{ $animate?: boolean }>`
  ${props =>
    props.$animate &&
    css`
      animation: ${rise} 0.24s ease-out both;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Chat = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  color: ${props => props.theme.colors.text01};
  font-family: ${props => props.theme.fonts.body};
`;

// Compact while the chat is empty, so the input sits right under the greeting instead of at the bottom.
export const Thread = styled.div<{ $compact?: boolean }>`
  flex: ${props => (props.$compact ? "0 1 auto" : "1")};
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 18px 16px 12px;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.colors.grey04} transparent;
`;

export const EmptyHeadline = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: ${props => props.theme.colors.text01};
  text-wrap: balance;
`;

export const EmptyBody = styled.div`
  color: ${props => props.theme.colors.text02};
  font-size: 14px;
  line-height: 22px;
  white-space: pre-wrap;
`;

// 18px makes the letters match the reply's x-height; the top margin puts their base on the first line's baseline.
export const Avatar = styled.span<{ $thinking?: boolean }>`
  flex-shrink: 0;
  display: flex;
  width: 18px;
  height: 18px;
  margin-top: 3px;

  svg {
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .letters {
    fill: ${WILLOW_MARK_NAVY};
  }

  .dot {
    fill: ${WILLOW_MARK_BLUE};
    transform-box: fill-box;
    transform-origin: 50% 100%;
  }

  ${props =>
    props.$thinking &&
    css`
      .dot {
        animation: ${hop} 0.95s ease-in-out infinite;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    .dot {
      animation: none;
    }
  }
`;

// A continued row belongs to the turn above it, so it sits closer and leaves the avatar column empty.
export const AssistantTurn = styled.div<{ $animate?: boolean; $continued?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 100%;
  margin-top: ${props => (props.$continued ? "-8px" : "0")};
  ${enter}
`;

export const AvatarSpacer = styled.span`
  flex-shrink: 0;
  width: 18px;
`;

export const ProfileLine = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: ${props => props.theme.colors.text02};
`;

export const AssistantMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  flex: 1;
  padding-top: 3px;
`;

export const AssistantText = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: ${props => props.theme.colors.text01};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const ReplyText = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: ${props => props.theme.colors.text01};
  overflow-wrap: anywhere;

  p,
  ul,
  ol {
    margin: 0 0 8px;
  }

  > :last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    padding-left: 20px;
  }

  li {
    margin: 2px 0;
  }

  li > p {
    margin: 0;
  }

  strong {
    font-weight: 600;
  }

  code {
    padding: 1px 4px;
    border-radius: 4px;
    background-color: ${props => props.theme.colors.grey05};
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
  }

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }
`;

export const Thinking = styled.div`
  align-self: flex-start;
  font-size: 13px;
  line-height: 22px;
  color: ${props => props.theme.colors.text03};
  background: linear-gradient(90deg, ${props => props.theme.colors.text03} 0%, ${props => props.theme.colors.text01} 50%, ${props => props.theme.colors.text03} 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 1.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    -webkit-text-fill-color: ${props => props.theme.colors.text03};
  }
`;

export const UserTurn = styled.div<{ $animate?: boolean }>`
  display: flex;
  justify-content: flex-end;
  ${enter}
`;

export const UserBubble = styled.div`
  max-width: 84%;
  padding: 10px 14px;
  border-radius: 18px 18px 6px 18px;
  background-color: ${props => props.theme.colors.grey02};
  color: ${props => props.theme.colors.text01};
  font-size: 14px;
  line-height: 22px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const EventLine = styled.div<{ $animate?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  color: ${props => props.theme.colors.text03};
  font-size: 11.5px;
  line-height: 16px;
  ${enter}

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background-color: ${props => props.theme.colors.grey03};
  }

  span {
    max-width: 80%;
    text-align: center;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

type ActionStatus = "pending" | "done" | "error";

export const Activity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ActionRow = styled.div<{ $status: ActionStatus }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: ${props => (props.$status === "error" ? props.theme.colors.red : props.theme.colors.text02)};
  font-size: 12.5px;
  line-height: 18px;
  overflow-wrap: anywhere;
  transition: color 0.2s ease;

  ${props =>
    props.$status === "error" &&
    css`
      padding: 6px 10px;
      border-radius: 10px;
      background-color: ${props.theme.colors.rebSubtle};
    `}
`;

export const ActionIcon = styled.span<{ $status: ActionStatus }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: ${props => (props.$status === "error" ? props.theme.colors.red : props.$status === "done" ? props.theme.colors.green : props.theme.colors.text03)};

  i {
    font-size: 12px;
    line-height: 1;
  }
`;

export const Spinner = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1.5px solid ${props => props.theme.colors.grey04};
  border-top-color: ${props => props.theme.colors.primary};
  animation: ${spin} 0.8s linear infinite;
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 2px;
`;

export const Chip = styled.button`
  max-width: 100%;
  border: 1px solid ${props => props.theme.colors.grey04};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text01};
  border-radius: 999px;
  padding: 7px 13px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  text-align: left;
  cursor: pointer;
  animation: ${rise} 0.24s ease-out both;
  transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;

  &:nth-child(2) {
    animation-delay: 0.05s;
  }

  &:nth-child(3) {
    animation-delay: 0.1s;
  }

  &:nth-child(4) {
    animation-delay: 0.15s;
  }

  &:nth-child(5) {
    animation-delay: 0.2s;
  }

  &:hover {
    background-color: ${props => props.theme.colors.grey01};
    border-color: ${props => props.theme.colors.grey08};
  }

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primaryLight};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Footer = styled.div`
  flex-shrink: 0;
  padding: 6px 12px 12px;
`;

export const InputBox = styled.form`
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.grey04};
  box-shadow: 0 1px 2px rgba(28, 30, 31, 0.04), 0 6px 16px rgba(28, 30, 31, 0.06);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primaryLight}, 0 6px 16px rgba(28, 30, 31, 0.06);
  }
`;

export const Input = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  max-height: 160px;
  padding: 12px 14px 4px;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font: inherit;
  font-size: 14px;
  line-height: 21px;
  color: ${props => props.theme.colors.text01};

  &::placeholder {
    color: ${props => props.theme.colors.text03};
  }
`;

export const InputActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px 8px;
`;

export const AttachButton = styled.button`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: ${props => props.theme.colors.text02};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.colors.grey01};
    color: ${props => props.theme.colors.text01};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primaryLight};
  }

  &:disabled {
    color: ${props => props.theme.colors.grey04};
    cursor: default;
  }
`;

export const ProfileTriggerWrap = styled.span`
  position: relative;
  display: inline-flex;
  max-width: 100%;
  vertical-align: top;
  border-radius: 999px;
`;

type ProfileTriggerVariant = "inline" | "compact";

export const ProfileTrigger = styled.button<{ $variant: ProfileTriggerVariant; $static?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: ${props => (props.$variant === "inline" ? "240px" : "200px")};
  height: ${props => (props.$variant === "inline" ? "24px" : "30px")};
  padding: ${props => (props.$variant === "inline" ? "0 8px 0 3px" : "0 8px 0 7px")};
  border: none;
  border-radius: 999px;
  background-color: ${props => (props.$variant === "inline" ? props.theme.colors.primarySubtle : "transparent")};
  color: ${props => (props.$variant === "inline" ? props.theme.colors.text01 : props.theme.colors.text02)};
  font: inherit;
  font-size: ${props => (props.$variant === "inline" ? "13.5px" : "12px")};
  font-weight: ${props => (props.$variant === "inline" ? 600 : 500)};
  line-height: 16px;
  cursor: ${props => (props.$static ? "default" : "pointer")};
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background-color: ${props => (props.$variant === "inline" ? props.theme.colors.primaryLight : props.theme.colors.grey01)};
    color: ${props => props.theme.colors.text01};
  }

  > svg {
    color: ${props => (props.$variant === "inline" ? props.theme.colors.primary : props.theme.colors.text02)};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primaryLight};
  }

  &:disabled {
    cursor: default;
  }

  .name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ProfileChevron = styled(ChevronDown)<{ $open?: boolean }>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-right: -2px;
  stroke-width: 2.5;
  transform: rotate(${props => (props.$open ? "180deg" : "0deg")});
  transition: transform 0.15s ease;
`;

// The same tile Create's profile cards use: the light-theme logo, or the profile's initial when it has none.
export const ProfileLogo = styled.span<{ $size: number; $hasImage: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${props => (props.$hasImage ? props.theme.colors.white : props.theme.colors.grey02)};
  box-shadow: inset 0 0 0 1px ${props => props.theme.colors.grey03};
  color: ${props => props.theme.colors.text02};
  font-size: ${props => Math.round(props.$size * 0.5)}px;
  font-weight: 700;
  line-height: 1;

  img {
    width: 78%;
    height: 78%;
    object-fit: contain;
  }
`;

export const ProfileMenuList = styled.div`
  position: fixed;
  z-index: 1000;
  box-sizing: border-box;
  min-width: 240px;
  max-width: 320px;
  max-height: 340px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid ${props => props.theme.colors.grey03};
  border-radius: 14px;
  background-color: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.shadowM};
  font-family: ${props => props.theme.fonts.body};
  animation: ${rise} 0.14s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ProfileMenuLabel = styled.div`
  padding: 6px 10px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.text03};
`;

export const ProfileMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 10px;
  background-color: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s ease;

  &:hover,
  &:focus-visible {
    outline: none;
    background-color: ${props => props.theme.colors.grey01};
  }
`;

export const ProfileMenuText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;

  strong,
  small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: 13.5px;
    font-weight: 500;
    line-height: 18px;
    color: ${props => props.theme.colors.text01};
  }

  small {
    font-size: 12px;
    line-height: 16px;
    color: ${props => props.theme.colors.text03};
  }
`;

export const ProfileMenuCheck = styled(Check)`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: ${props => props.theme.colors.primary};
`;

// Indented past the action icon, so the chips sit under the step they belong to.
export const TaggedList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding-left: 26px;

  .label {
    font-size: 12px;
    line-height: 18px;
    color: ${props => props.theme.colors.text03};
  }
`;

export const TaggedChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  height: 26px;
  padding: 0 10px 0 4px;
  border: 1px solid ${props => props.theme.colors.grey04};
  border-radius: 999px;
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text01};
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 16px;
  cursor: default;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.grey08};
    background-color: ${props => props.theme.colors.grey05};
  }

  &:focus-visible {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primaryLight};
  }

  .name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// People pictures are photos, so they fill the circle instead of sitting inside it like a logo.
export const TaggedAvatarImage = styled.img`
  && {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const TaggedCard = styled.div`
  position: fixed;
  z-index: 1000;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid ${props => props.theme.colors.grey03};
  border-radius: 14px;
  background-color: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.shadowM};
  font-family: ${props => props.theme.fonts.body};
  pointer-events: none;
  animation: ${rise} 0.14s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const TaggedCardText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: ${props => props.theme.colors.text01};
    overflow-wrap: anywhere;
  }

  span {
    font-size: 12.5px;
    line-height: 18px;
    color: ${props => props.theme.colors.text02};
    overflow-wrap: anywhere;
  }

  small {
    margin-top: 2px;
    font-size: 11.5px;
    line-height: 16px;
    color: ${props => props.theme.colors.text03};
  }
`;

export const SendButton = styled.button`
  flex-shrink: 0;
  margin-left: auto;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.colors.primaryHover};
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primaryLight};
  }

  &:disabled {
    background-color: ${props => props.theme.colors.grey03};
    color: ${props => props.theme.colors.white};
    cursor: default;
    transform: none;
  }
`;

export const ErrorText = styled.div`
  align-self: center;
  padding: 4px 10px;
  border-radius: 10px;
  background-color: ${props => props.theme.colors.rebSubtle};
  color: ${props => props.theme.colors.red};
  font-size: 12px;
  line-height: 16px;
`;

export const DropHint = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed ${props => props.theme.colors.primary};
  border-radius: 16px;
  background-color: rgba(255, 255, 255, 0.92);
  color: ${props => props.theme.colors.primary};
  font-size: 14px;
  font-weight: 500;
  pointer-events: none;
`;

export const UploadList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

export const UploadChip = styled.div<{ $status: "uploading" | "ready" | "failed" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid ${props => (props.$status === "failed" ? props.theme.colors.rebSubtle : props.theme.colors.grey03)};
  background-color: ${props => (props.$status === "failed" ? props.theme.colors.rebSubtle : props.theme.colors.white)};
  color: ${props => (props.$status === "failed" ? props.theme.colors.red : props.theme.colors.text01)};
  font-size: 12px;
  line-height: 16px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const RemoveUpload = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: ${props => props.theme.colors.text03};
  font-size: 14px;
  line-height: 16px;
  cursor: pointer;
`;

export const MessageFiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
`;

export const MessageFile = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid ${props => props.theme.colors.grey03};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text02};
  font-size: 12px;
  line-height: 16px;
`;
